// AP-167 · Workflow tracker do Portal — totalmente localizado via usePortalI18n.
// Diferentemente de /pages/processes/WorkflowTracker.jsx (que é usado pelo
// admin ERP e permanece em pt-BR por design), este componente respeita o
// idioma selecionado pelo cliente em TODOS os textos: título, etapa atual
// e rótulos de cada passo.
import React from "react";
import { Check } from "lucide-react";
import { usePortalI18n } from "./PortalI18nContext";
import { PORTAL_STAGE_KEYS, normalizeStage } from "./portalProcessStatus";

const STATE_STYLE = {
  completed: {
    circle:  "bg-emerald-500 border-emerald-500 text-white",
    label:   "text-emerald-700",
    connect: "bg-emerald-500",
  },
  current: {
    circle:  "bg-blue-500 border-blue-500 text-white ring-4 ring-blue-100 scale-110",
    label:   "text-blue-700 font-semibold",
    connect: "bg-gray-300",
  },
  pending: {
    circle:  "bg-white border-gray-300 text-gray-400",
    label:   "text-gray-500",
    connect: "bg-gray-300",
  },
};

export default function PortalWorkflowTracker({ value }) {
  const { t } = usePortalI18n();
  const stage = normalizeStage(value);
  const currentIndex = Math.max(0, PORTAL_STAGE_KEYS.indexOf(stage));
  const stages = PORTAL_STAGE_KEYS.map((k) => ({ key: k, label: t(`stage.${k}.label`) }));

  return (
    <section
      data-testid="portal-workflow-tracker"
      aria-label={t("workflow.title")}
      className="rounded-2xl border border-border bg-white p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t("workflow.title")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("workflow.currentStage")}:{" "}
            <span className="font-medium text-foreground">{stages[currentIndex].label}</span>
          </p>
        </div>
      </div>

      <ol className="flex items-start justify-between gap-1" data-testid="portal-workflow-stages">
        {stages.map((s, idx) => {
          const state = idx < currentIndex ? "completed" : idx === currentIndex ? "current" : "pending";
          const st = STATE_STYLE[state];
          const isLast = idx === stages.length - 1;
          const nextState = idx + 1 < currentIndex ? "completed" : "pending";
          const connectorClass = STATE_STYLE[nextState].connect;

          return (
            <li key={s.key} className="flex-1 flex flex-col items-center relative">
              {!isLast && (
                <span
                  aria-hidden
                  data-testid={`portal-workflow-connector-${s.key}`}
                  className={`absolute top-4 left-1/2 h-[3px] w-full ${connectorClass}`}
                />
              )}
              <div
                data-testid={`portal-workflow-stage-${s.key}`}
                data-state={state}
                className="relative z-10 flex flex-col items-center gap-2"
              >
                <span
                  className={`h-9 w-9 rounded-full border-2 grid place-items-center transition-all duration-200 ${st.circle}`}
                >
                  {state === "completed" ? (
                    <Check className="h-4 w-4" strokeWidth={3} />
                  ) : state === "current" ? (
                    <span className="h-2 w-2 rounded-full bg-white" />
                  ) : (
                    <span className="h-2 w-2 rounded-full bg-gray-300" />
                  )}
                </span>
                <span className={`text-[11px] md:text-xs text-center ${st.label}`}>{s.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
